import { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon, LinkIcon } from '@heroicons/react/24/outline';
import type { Share } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { revokeShare } from './api';

/**
 * Liste des partages — UN composant, deux emplacements : « Mes partages »
 * (Paramètres, alimenté par `/api/shares/mine`) et la console admin
 * (`/admin/partages`, alimentée par la route d'administration).
 *
 * La seule différence visible est la colonne d'AUTEUR, affichée quand la liste en
 * mélange plusieurs. Le reste est identique parce que la question posée est la même :
 * quels liens publics existent, lesquels sont encore vivants, et lesquels dois-je
 * couper ?
 *
 * ── LE COMPTEUR DE VUES N'EST PAS DÉCORATIF ───────────────────────────────────────
 *
 * C'est le seul signal qui dise qu'un lien oublié depuis un an est toujours consulté.
 * Un partage sans échéance et à 200 vues mérite un regard ; le même à 0 vue n'est
 * qu'un oubli sans conséquence.
 */

type ShareStatus = 'live' | 'expired' | 'revoked';

/** État d'un partage, dérivé des mêmes règles que la résolution publique. */
function shareStatus(share: Share, now: Date): ShareStatus {
  if (share.revokedAt !== null) return 'revoked';
  if (share.expiresAt !== null && new Date(share.expiresAt).getTime() <= now.getTime()) {
    return 'expired';
  }
  return 'live';
}

interface SharesListProps {
  /** Source des données — injectée, c'est ce qui rend le composant réutilisable. */
  load: () => Promise<Share[]>;
  /** Affiche l'auteur de chaque partage (console admin). */
  showAuthor: boolean;
}

export function SharesList({ load, showAuthor }: SharesListProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const [shares, setShares] = useState<Share[] | null>(null);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setError(false);
    try {
      setShares(await load());
    } catch {
      setError(true);
    }
  }, [load]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRevoke = async (share: Share): Promise<void> => {
    setBusyId(share.id);
    try {
      await revokeShare(share.id);
      await refresh();
    } catch {
      setError(true);
    } finally {
      setBusyId(null);
    }
  };

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-danger">{t('share.list.failed')}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border-strong px-3 py-1.5 text-sm text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t('share.list.retry')}
        </button>
      </div>
    );
  }

  if (shares === null) {
    return (
      <p role="status" className="py-8 text-center text-sm text-muted">
        {t('share.list.loading')}
      </p>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <LinkIcon className="h-9 w-9 text-muted" aria-hidden="true" />
        <p className="text-sm text-muted">{t('share.list.empty')}</p>
      </div>
    );
  }

  const now = new Date();
  const dateOf = (iso: string): string => new Date(iso).toLocaleDateString(locale);

  return (
    <ul className="divide-y divide-border">
      {shares.map((share) => {
        const status = shareStatus(share, now);
        return (
          <li key={share.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{share.title}</p>
              <p className="mt-0.5 truncate font-mono text-xs tabular-nums text-muted">
                {[
                  showAuthor ? share.createdByEmail : null,
                  interpolate(t('share.list.mediaCount'), { count: share.assetCount }),
                  interpolate(t('share.list.viewCount'), { count: share.viewCount }),
                  share.expiresAt === null
                    ? t('share.list.noExpiry')
                    : interpolate(t('share.list.expiresOn'), { date: dateOf(share.expiresAt) }),
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
              </p>
            </div>

            {/* L'état passe par le TEXTE de la pastille, jamais par sa seule couleur. */}
            <span
              className={`shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                status === 'live'
                  ? 'border-success/50 bg-success/10 text-success'
                  : 'border-border-strong text-muted'
              }`}
            >
              {t(`share.list.status.${status}`)}
            </span>

            {status !== 'revoked' && (
              <button
                type="button"
                onClick={() => void onRevoke(share)}
                disabled={busyId === share.id}
                className="shrink-0 rounded-md px-2 py-1 text-sm font-semibold text-danger hover:bg-danger/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('share.list.revoke')}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
